import React from "react";
import { Box, Button, EmptyState, OrgChart, Skeleton, Text } from "@salestrak/ui";
import { OrgChartPersonContent } from "#app/components/org-chart/org-chart-person-content";
import {
  useReportingLine,
  type OrgChartUserItem,
} from "#app/components/org-chart/use-org-chart-data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { PanelBanner } from "./panel-banner";
import { AnalyticsContext, OrgChartEvent, trackEvent } from "#app/lib/analytics";

/**
 * Org Chart tab — the member's slice of the reporting line, plus the banner
 * that jumps to the full chart focused on them. The canvas grows to eat all
 * remaining height and the Explore banner pins to the bottom — no dead space
 * below either.
 */
export function MemberProfileOrgChartTab({
  userId,
  isSelf,
  onViewMember,
  onClose,
}: {
  userId: string | null;
  isSelf: boolean;
  onViewMember?: ((userId: string) => void) | undefined;
  onClose: () => void;
}) {
  const { push } = useAppNavigate();
  const reportingLine = useReportingLine(userId);
  const renderOrgNode = React.useCallback(
    (item: OrgChartUserItem) =>
      item.data.kind === "placeholder" ? (
        <Text size="sm" className="text-info-foreground">
          No manager
        </Text>
      ) : (
        <OrgChartPersonContent user={item.data.user} />
      ),
    [],
  );
  const handleOrgNodePress = React.useCallback(
    (id: string) => {
      if (id !== userId) onViewMember?.(id);
    },
    [userId, onViewMember],
  );
  const handleExploreOrgChart = React.useCallback(() => {
    if (!userId) return;
    trackEvent({ context: AnalyticsContext.OrgChart, event: OrgChartEvent.ExploreFromProfile });
    // Close the island so the full chart owns the screen; the page rings the
    // focused member off the `focus` param once no profile is active.
    push(`/org-chart?focus=${userId}`);
    onClose();
  }, [push, userId, onClose]);

  return (
    <Box direction="column" fill gap="md" paddingX="md" paddingY="md">
      {!reportingLine.ready ? (
        // Chart + banner placeholders while the charted member pool the tree
        // builds from is still loading. Delayed: a cached pool lands inside
        // the window and no placeholder ever shows.
        <>
          <Box fill minHeight={280} paddingTop="md">
            <Box direction="column" gap="md">
              <Skeleton delayed height={56} width={224} />
              <Box paddingLeft="xl">
                <Skeleton delayed height={56} width={224} />
              </Box>
              <Box paddingLeft="xl">
                <Skeleton delayed height={56} width={224} />
              </Box>
            </Box>
          </Box>
          <Skeleton delayed height={64} width={320} />
        </>
      ) : reportingLine.items.length > 0 ? (
        <>
          {/* Flex column, not a bare (block) Box: the chart's own `fill`
              is flex-1 and collapses to 0 height in a block parent — React
              Flow then renders nothing (error #004). */}
          <Box direction="column" fill minHeight={280}>
            <OrgChart
              // Reset collapse state + viewport when the surface swaps members.
              key={userId ?? "none"}
              controls={false}
              defaultCollapsedIds={reportingLine.defaultCollapsedIds}
              // Embedded in the panel: the dots would read as a second
              // canvas surface behind the panel's own.
              dotGrid={false}
              items={reportingLine.items}
              onNodePress={handleOrgNodePress}
              renderNode={renderOrgNode}
              selectedId={userId}
            />
          </Box>
          <PanelBanner
            actions={
              <Button onClick={handleExploreOrgChart} size="sm">
                Explore
              </Button>
            }
            description={`${
              reportingLine.count === 1
                ? "1 member"
                : `${reportingLine.count} members`
            } in ${isSelf ? "your" : "this"} reporting line`}
            icon="network"
            title="Explore the full org chart"
          />
        </>
      ) : (
        <EmptyState
          description="Only active and invited members appear here."
          icon="network"
          media="circle"
          size="sm"
          title="Not in the org chart"
        />
      )}
    </Box>
  );
}
